import { deleteCookie, setCookie } from '$lib/utils/cookies';
import { COOKIE_MAX_AGE, getCookieDomain, navigateToOrgSubdomain } from '$lib/utils/domain';
import { getContext, setContext } from 'svelte';

/**
 * Context for tracking the currently selected organization context.
 * Persists to a cookie to maintain selection across page refreshes.
 * Handles subdomain navigation when organization changes.
 */
export const organizationCookieName = 'hessian-organization-id';
const ORGANIZATION_CONTEXT_KEY = Symbol('organization');

export interface OrganizationState {
	id: string | undefined;
	slug: string | null | undefined;
	personal: boolean | undefined;
}

export const setOrganizationContext = (
	getInitialState?: () => string | Partial<OrganizationState> | undefined,
	options?: { persist?: boolean }
) => {
	// Support both string (just ID) and object formats for backwards compatibility
	const initialState = getInitialState?.();
	const initialId = typeof initialState === 'string' ? initialState : initialState?.id;
	const initialSlug = typeof initialState === 'string' ? undefined : initialState?.slug;
	const initialPersonal = typeof initialState === 'string' ? undefined : initialState?.personal;
	const persist = options?.persist ?? true;
	let navigated = false;

	const organization = $state<OrganizationState>({
		id: initialId,
		slug: initialSlug,
		personal: initialPersonal
	});

	$effect(() => {
		if (typeof window === 'undefined' || navigated || !persist) return;

		try {
			if (organization.id) {
				// Persist selection to cookie
				const cookieDomain = getCookieDomain(window.location.hostname);
				setCookie(organizationCookieName, organization.id, {
					path: '/',
					domain: cookieDomain,
					sameSite: 'Lax',
					secure: true,
					maxAge: COOKIE_MAX_AGE > 0 ? COOKIE_MAX_AGE : undefined
				});

				const hasRoutingInfo = organization.personal === true || organization.slug !== undefined;

				// Navigate to correct subdomain if needed.
				// Only check once we have org data loaded (slug/personal are undefined
				// until the team-switcher fetches org details from the API).
				if (hasRoutingInfo) {
					const orgSlug = organization.personal ? null : organization.slug!;

					navigated = navigateToOrgSubdomain(
						organization.id,
						orgSlug,
						organizationCookieName,
						COOKIE_MAX_AGE > 0 ? COOKIE_MAX_AGE : undefined
					);
				}
			} else {
				const cookieDomain = getCookieDomain(window.location.hostname);
				deleteCookie(organizationCookieName, { path: '/', domain: cookieDomain });
			}
		} catch (e) {
			console.warn('Failed to persist organization selection:', e);
		}
	});

	setContext(ORGANIZATION_CONTEXT_KEY, organization);

	return organization;
};

export const getOrganizationContext = () => {
	return getContext<OrganizationState>(ORGANIZATION_CONTEXT_KEY);
};
